import type {
  QuickQuoteCreate,
  QuickQuoteResponse,
  QuoteCreate,
  QuoteDetail,
  QuotesListResponse,
  QuotesSortField,
  QuoteStatus,
  QuoteType,
  QuoteUpdate,
  QuotePromoteResponse,
  QuoteOptionSearchResult,
  GenerateTicketResponse,
  LinkInvoiceResponse,
  LinkTicketResponse,
} from '@techgitter/contracts';

const API_BASE_URL = '/api';

export interface FetchQuotesParams {
  page: number;
  pageSize: number;
  sort?: QuotesSortField;
  order?: 'asc' | 'desc';
  search?: string;
  status?: QuoteStatus | QuoteStatus[];
  type?: QuoteType;                          // Filter by quote category
  linkage?: 'ticket' | 'standalone';         // Filter by ticket-linkage state
  customerId?: string;
  jobId?: string;
  variationGroupId?: string;                 // Fetch siblings in a variation set
  signal?: AbortSignal;
}

export async function fetchQuotes(
  params: FetchQuotesParams,
): Promise<QuotesListResponse> {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page));
  qs.set('pageSize', String(params.pageSize));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.search) qs.set('search', params.search);
  if (params.status) {
    const statusValue = Array.isArray(params.status)
      ? params.status.join(',')
      : params.status;
    if (statusValue) qs.set('status', statusValue);
  }
  if (params.type) qs.set('type', params.type);
  if (params.linkage) qs.set('linkage', params.linkage);
  if (params.customerId) qs.set('customerId', params.customerId);
  if (params.jobId) qs.set('jobId', params.jobId);
  if (params.variationGroupId) {
    qs.set('variationGroupId', params.variationGroupId);
  }

  const response = await fetch(`${API_BASE_URL}/quotes?${qs}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal: params.signal,
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch quotes: ${response.status} ${response.statusText}`,
    );
  }

  const data = await response.json();
  return {
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: 1,
      pageSize: 50,
      totalItems: 0,
      totalPages: 0,
      hasNext: false,
      hasPrevious: false,
    },
  };
}

export async function fetchQuoteDetail(
  quoteNumber: string,
  signal?: AbortSignal,
): Promise<QuoteDetail> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to fetch quote ${quoteNumber}: ${response.status} ${response.statusText}`,
    );
  }
  return response.json();
}

export async function createQuote(
  payload: QuoteCreate,
  signal?: AbortSignal,
): Promise<QuoteDetail> {
  const response = await fetch(`${API_BASE_URL}/quotes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Failed to create quote: ${response.status} ${text}`);
  }
  return response.json();
}

export async function createQuickQuote(
  payload: QuickQuoteCreate,
  signal?: AbortSignal,
): Promise<QuickQuoteResponse> {
  const response = await fetch(`${API_BASE_URL}/quotes/quick`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    signal,
  });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Failed to create quick quote: ${response.status} ${text}`);
  }
  return response.json();
}

export async function updateQuote(
  quoteNumber: string,
  updates: QuoteUpdate,
  signal?: AbortSignal,
): Promise<QuoteDetail> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to update quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

export async function deleteQuote(
  quoteNumber: string,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}`,
    { method: 'DELETE', signal },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to delete quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
}

export async function promoteQuote(
  quoteNumber: string,
  options?: {
    invoiceName?: string;
    lineDescriptions?: Record<string, string>;
    signal?: AbortSignal;
  },
): Promise<QuotePromoteResponse & { messageId?: string; status?: string }> {
  const body: Record<string, unknown> = {};
  // Only send the override when the caller actually supplied one;
  // an empty string means "use the stored quote name" — handled by
  // the backend the same as omitting the field.
  if (options?.invoiceName && options.invoiceName.trim()) {
    body.invoiceName = options.invoiceName.trim();
  }
  // Transient per-line description overrides (keyed by service/item id
  // or "consolidated"). Send only non-empty entries; the backend also
  // sanitises, so a blank slips through harmlessly.
  if (options?.lineDescriptions) {
    const cleaned: Record<string, string> = {};
    for (const [key, value] of Object.entries(options.lineDescriptions)) {
      if (value && value.trim()) cleaned[key] = value.trim();
    }
    if (Object.keys(cleaned).length > 0) body.lineDescriptions = cleaned;
  }
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/promote`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: options?.signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to promote quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

/**
 * Convert a quote into a RepairShopr ticket. Terminal like promotion — the
 * quote is locked afterwards. The RS ticket is created asynchronously, so the
 * ticket number arrives later via a `quotes:changed` / `ticket_generated`
 * SignalR event, not in this 202 response. When `includeLineItems` is true the
 * quote's marked line items are carried onto the ticket.
 */
export async function generateTicket(
  quoteNumber: string,
  options: { includeLineItems: boolean; subject?: string; signal?: AbortSignal },
): Promise<GenerateTicketResponse> {
  const body: Record<string, unknown> = {
    includeLineItems: options.includeLineItems,
  };
  if (options.subject && options.subject.trim()) {
    body.subject = options.subject.trim();
  }
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/generate-ticket`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: options.signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to generate ticket from quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

/**
 * Link a quote to an EXISTING invoice (one raised manually, not by SM4RT).
 * No invoice is created and no line items are added — the quote is bound to
 * the invoice and marked terminal (locked). Returns the bound invoice
 * identifiers plus an optional `warning` (e.g. customer mismatch).
 */
export async function linkInvoice(
  quoteNumber: string,
  invoiceNumber: string,
  signal?: AbortSignal,
): Promise<LinkInvoiceResponse> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/link-invoice`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ invoiceNumber }),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to link invoice ${invoiceNumber} to quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

/**
 * Link a quote to an EXISTING RepairShopr ticket (a Job raised outside the
 * quote flow). No ticket is created and no line items are added — the quote is
 * bound to the ticket and marked terminal (locked). Returns the bound ticket
 * identifiers plus an optional `warning` (e.g. customer mismatch).
 */
export async function linkTicket(
  quoteNumber: string,
  ticketNumber: string,
  signal?: AbortSignal,
): Promise<LinkTicketResponse> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/link-ticket`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ticketNumber }),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to link ticket ${ticketNumber} to quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

/**
 * Create a *variation* of a quote — a sibling for the SAME customer that
 * keeps the ticket/check-in/device linkage and shares a variationGroupId
 * with the source. Returns the new draft quote.
 */
export async function createQuoteVariation(
  quoteNumber: string,
  signal?: AbortSignal,
): Promise<QuoteDetail> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/variation`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, signal },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to create variation of quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

/**
 * Duplicate a quote for a DIFFERENT customer. Copies the line items and
 * pricing settings, drops all source-customer linkage, and assigns the
 * given customer. Returns the new draft quote.
 */
export async function duplicateQuote(
  quoteNumber: string,
  customerId: string,
  signal?: AbortSignal,
): Promise<QuoteDetail> {
  const response = await fetch(
    `${API_BASE_URL}/quotes/${encodeURIComponent(quoteNumber)}/duplicate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId }),
      signal,
    },
  );
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(
      `Failed to duplicate quote ${quoteNumber}: ${response.status} ${text}`,
    );
  }
  return response.json();
}

export async function searchQuoteOptions(
  query: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<QuoteOptionSearchResult[]> {
  const qs = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await fetch(
    `${API_BASE_URL}/quote-options/search?${qs}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error(
      `Failed to search quote options: ${response.status} ${response.statusText}`,
    );
  }
  const data = await response.json();
  return data.results ?? [];
}
